/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import * as THREE from 'three';

import {
	LeaderboardComponent,
	MultiplayerStateComponent,
	RemotePlayerComponent,
} from '../../components/MultiplayerComponents';

import { Client } from '@colyseus/sdk';
import { PlayerStateComponent } from '../../components/PlayerStateComponent';
import { System } from 'ecsy';
import { getOnlyEntity } from '../../utils/entityUtils';

// Injected at build time by webpack DefinePlugin (see webpack.config.js).
// Dev: ws://localhost:2567. Prod: the Cloud Run wss:// URL set by the deploy script.
const MULTIPLAYER_SERVER_URL =
	process.env.MULTIPLAYER_URL || 'ws://localhost:2567';

// Rate-limit position updates to the server. Rendering runs at 72+ Hz, but
// streaming transforms that often is wasteful; ~15 Hz is plenty since remote
// players are interpolated client-side (see RemotePlayerSystem).
const MOVE_SEND_INTERVAL = 1 / 15; // seconds

const RANDOM_NAMES = [
	'Sunflow',
	'Rosebud',
	'TulipTop',
	'DaisyDash',
	'FernFox',
	'IvyLeaf',
	'OakRoot',
	'PineCone',
	'LilyPad',
	'Mossy',
];

export class MultiplayerSystem extends System {
	init() {
		this.client = new Client(MULTIPLAYER_SERVER_URL);
		this.room = null;
		this._moveSendAccumulator = 0;

		const multiplayerEntity = this.world.createEntity();
		multiplayerEntity.addComponent(MultiplayerStateComponent, {
			client: this.client,
			localPlayerName:
				RANDOM_NAMES[Math.floor(Math.random() * RANDOM_NAMES.length)] +
				'#' +
				Math.floor(Math.random() * 1000),
		});

		this.multiplayerState = multiplayerEntity.getMutableComponent(
			MultiplayerStateComponent,
		);

		this._fetchCountryData();
		this._connect();
	}

	async _fetchCountryData() {
		try {
			const response = await fetch('https://ipapi.co/json/');
			const data = await response.json();
			this.multiplayerState.localPlayerCountry = data.country_name || 'Unknown';
		} catch (e) {
			console.warn('Failed to fetch country data', e);
			this.multiplayerState.localPlayerCountry = 'Unknown';
		}
	}

	async _connect() {
		try {
			this.room = await this.client.joinOrCreate('flowerbed_room', {
				name: this.multiplayerState.localPlayerName || 'Anonymous',
				country: this.multiplayerState.localPlayerCountry || 'Unknown',
			});
			this.multiplayerState.room = this.room;
			this.multiplayerState.isConnected = true;
			console.log('Successfully joined Colyseus room:', this.room.name);

			this.room.state.players.onAdd((player, sessionId) => {
				if (sessionId === this.room.sessionId) return;
				this._createRemotePlayer(player, sessionId);

				player.onChange(() => {
					this._updateRemotePlayerData(player, sessionId);
				});
			});

			this.room.state.players.onRemove((_player, sessionId) => {
				this._removeRemotePlayer(sessionId);
			});

			this.room.onMessage('leaderboard_update', (data) => {
				this._updateLeaderboard(data);
			});

			console.log('Connected to Colyseus room:', this.room.name);
		} catch (e) {
			console.error('Colyseus connection failed', e);
		}
	}

	_createRemotePlayer(player, sessionId) {
		const entity = this.world.createEntity();
		entity.addComponent(RemotePlayerComponent, {
			id: sessionId,
			name: player.name,
			country: player.country,
			sessionId: sessionId,
			targetPos: new THREE.Vector3().copy(player.pos),
			targetRot: new THREE.Quaternion().copy(player.rot),
			targetHeadPos: new THREE.Vector3().copy(player.headPos),
			targetHeadRot: new THREE.Quaternion().copy(player.headRot),
		});
		console.log('Remote player joined:', player.name);
	}

	_updateRemotePlayerData(player, sessionId) {
		const entity = this.queries.remotePlayers.results.find(
			(e) => e.getComponent(RemotePlayerComponent).sessionId === sessionId,
		);
		if (entity) {
			const remotePlayer = entity.getMutableComponent(RemotePlayerComponent);
			remotePlayer.targetPos.copy(player.pos);
			remotePlayer.targetRot.copy(player.rot);
			remotePlayer.targetHeadPos.copy(player.headPos);
			remotePlayer.targetHeadRot.copy(player.headRot);
		}
	}

	_removeRemotePlayer(sessionId) {
		const remotePlayers = this.queries.remotePlayers.results;
		remotePlayers.forEach((entity) => {
			if (entity.getComponent(RemotePlayerComponent).sessionId === sessionId) {
				entity.remove();
			}
		});
	}

	_updateLeaderboard(data) {
		let leaderboardEntity = getOnlyEntity(this.queries.leaderboard, false);
		if (!leaderboardEntity) {
			leaderboardEntity = this.world.createEntity();
			leaderboardEntity.addComponent(LeaderboardComponent);
		}
		const leaderboard = leaderboardEntity.getMutableComponent(
			LeaderboardComponent,
		);
		leaderboard.totalVisitors = data.totalVisitors;
		leaderboard.visitorCountries = data.visitorCountries;
	}

	execute(delta, _time) {
		if (!this.multiplayerState.isConnected || !this.room) return;

		// throttle transform updates to MOVE_SEND_INTERVAL (~15 Hz)
		this._moveSendAccumulator += delta;
		if (this._moveSendAccumulator < MOVE_SEND_INTERVAL) return;
		this._moveSendAccumulator = 0;

		const playerEntity = getOnlyEntity(this.queries.player, false);
		if (!playerEntity) return;

		const playerState = playerEntity.getComponent(PlayerStateComponent);
		const { viewerTransform, playerHead } = playerState;

		// Send local player transform to server
		this.room.send('move', {
			pos: viewerTransform.position,
			rot: viewerTransform.quaternion,
			headPos: playerHead.position,
			headRot: playerHead.quaternion,
		});
	}
}

MultiplayerSystem.queries = {
	player: { components: [PlayerStateComponent] },
	remotePlayers: { components: [RemotePlayerComponent] },
	leaderboard: { components: [LeaderboardComponent] },
};
